import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { makeStyles } from "tss-react/mui";
import { MiAutocomplete, MiChip, MiIcon } from "@miplatform/components";
import { ICONS_WEB, STANDARD_DATE_FORMAT } from "@miplatform/constants";
import { useDebounce } from "@miplatform/hooks";
import {
  getContrastColor,
  getDurationInHours,
  isEmpty,
} from "@miplatform/utils";
import * as mdi from "@mdi/js";
import {
  Box,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { TaskOverview } from "../TaskOverview/TaskOverview";
import { useAttemptSearchViewModel } from "./useAttemptSearchViewModel";

export const AttemptSearch = ({
  searchableOrderTemplateIds,
  appendOrderTypeColor,
  buckets,
  onSelect,
  attachEventTimes,
}) => {
  const [anchorEl, setAnchorEl] = useState();

  const [highlightedOptionId, setHighlightedOptionId] = useState();
  useState();
  const debouncedOptionId = useDebounce(highlightedOptionId);

  const {
    attempts,
    search,
    debouncedSearch,
    stateManager,
    setSearch,
    getAttempts,
  } = useAttemptSearchViewModel({
    appendOrderTypeColor,
    searchableOrderTemplateIds,
  });

  const additionalData = useMemo(
    () =>
      buckets.reduce(
        (accu, bucket) => [
          ...accu,
          ...bucket.data
            .filter(
              (item) =>
                !debouncedSearch ||
                (item.home?.address?.addressLine1 || "")
                  .toLowerCase()
                  .includes(debouncedSearch.toLowerCase())
            )
            .map((e) => ({
              ...e,
              isInBucket: true,
              bucketName: bucket.name,
            })),
        ],
        []
      ),
    [buckets, debouncedSearch]
  );

  const options = attachEventTimes([...attempts, ...additionalData]).map(
    (option) => ({
      ...option,
      duration: getDurationInHours(
        option.displayStartTime,
        option.displayEndTime
      ),
    })
  );

  const attemptOnHighlight = useMemo(
    () => options[String(debouncedOptionId)?.split("-")?.at(-1)],
    [options, debouncedOptionId]
  );

  useEffect(() => {
    if (debouncedSearch) {
      getAttempts();
    }
  }, [debouncedSearch, appendOrderTypeColor]);

  const { classes, theme } = useStyles();

  const handleClear = () => {
    setSearch("");
    onSelect("");
  };

  const openPopper = () => {
    setAnchorEl(document.getElementById(debouncedOptionId));
  };

  const closePopper = () => {
    setHighlightedOptionId();
    setAnchorEl(null);
  };

  useEffect(() => {
    if (debouncedOptionId) {
      openPopper();
    }
  }, [debouncedOptionId]);

  return (
    <Box className={classes.root}>
      <MiAutocomplete
        blurOnSelect
        openOnFocus
        options={options}
        loading={stateManager.isBusy()}
        loadingText="Loading Attempts..."
        inputValue={search}
        onInputChange={(_, value) => {
          setSearch(value.trimStart());
        }}
        onChange={(_, value) => {
          onSelect(value);
          closePopper();
        }}
        getOptionLabel={(option) => option.home?.address?.addressLine1}
        handleClear={handleClear}
        forcePopupIcon={false}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              placeholder="Search Orders"
              fullWidth
              size="small"
              onKeyUp={(e) => {
                setHighlightedOptionId(
                  e.target.getAttribute("aria-activedescendant")
                );
              }}
              slotProps={{
                input: {
                  ...params.InputProps,
                  style: {
                    padding: theme.spacing(0.5),
                  },
                  inputProps: {
                    "data-testid": "attempt-search-input",
                    ...params.inputProps,
                  },
                  startAdornment: (
                    <InputAdornment position="end">
                      <MiIcon path={mdi[ICONS_WEB.MAGNIFY]} />
                    </InputAdornment>
                  ),
                  endAdornment: stateManager.isBusy() ? (
                    <InputAdornment position="end">
                      <MiIcon
                        path={mdi.mdiLoading}
                        spin={0.75}
                        color={theme.palette.blue.main}
                      />
                    </InputAdornment>
                  ) : null,
                },
              }}
            />
          );
        }}
        renderOption={(props, option) => {
          return (
            <MenuItem
              {...props}
              key={option.id}
              className={classes.menuItem}
              onMouseEnter={(e) => {
                setHighlightedOptionId(e.currentTarget.id);
              }}
              onMouseLeave={closePopper}
            >
              <Box display="flex" alignItems="center" gap={2} flex={1}>
                <MiChip
                  className={classes.chip}
                  style={{
                    backgroundColor:
                      option.assignment?.order?.orderTemplateColor,
                    color: getContrastColor(
                      option.assignment?.order?.orderTemplateColor
                    ),
                  }}
                  label={option.assignment.order?.orderTemplateAbbreviation}
                  size="small"
                />
                <Typography noWrap maxWidth={150}>
                  {option.home?.address?.addressLine1}
                </Typography>
                <Typography>#{option.attemptNumber}</Typography>
              </Box>
              <Typography
                color="grey.medium"
                fontSize={theme.fontSize.medium}
                alignSelf="flex-end"
              >
                {option.isInBucket
                  ? option.bucketName
                  : moment(option.scheduledStartDate).format(
                      STANDARD_DATE_FORMAT
                    )}
              </Typography>
            </MenuItem>
          );
        }}
      />
      {!isEmpty(attemptOnHighlight) && (
        <TaskOverview
          hideEmail
          attempt={attemptOnHighlight}
          issues={attemptOnHighlight.issues}
          anchorEl={anchorEl}
          closePopper={closePopper}
          isInBucket={attemptOnHighlight.isInBucket}
        />
      )}
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: { width: 400 },
  menuItem: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    whiteSpace: "nowrap",
    overflow: "hidden",
  },
  chip: {
    fontSize: theme.fontSize.xsmall,
    borderRadius: theme.spacing(1),
    height: 20,
    minWidth: 22,
  },
}));
